import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useLeague } from '@/contexts/LeagueContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { Trophy, Users, Layers } from 'lucide-react';
import { cn } from '@/lib/utils';
import MLBPlayerAvatar from './MLBPlayerAvatar';

interface DraftPick {
  id: string;
  round: number;
  pick_number: number;
  round_pick: number;
  team_id: string;
  team_name: string;
  player_id: string;
  player_name: string;
  position: string;
  mlb_team: string | null;
  headshot_url: string | null;
  is_auto_pick: boolean;
}

const positionColors: Record<string, string> = {
  C: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  '1B': 'bg-green-500/20 text-green-400 border-green-500/30',
  '2B': 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  '3B': 'bg-red-500/20 text-red-400 border-red-500/30',
  SS: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  LF: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
  CF: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
  RF: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
  OF: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
  DH: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  SP: 'bg-red-500/20 text-red-400 border-red-500/30',
  RP: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  P: 'bg-red-500/20 text-red-400 border-red-500/30',
};

interface DraftResultsProps {
  draftId: string;
}

const DraftResults = ({ draftId }: DraftResultsProps) => {
  const { teams: leagueTeams } = useLeague();
  const [picks, setPicks] = useState<DraftPick[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<'round' | 'team'>('round');
  const [selectedTeamId, setSelectedTeamId] = useState<string | null>(null);

  const fetchPicks = useCallback(async () => {
    const { data, error } = await supabase
      .from('draft_picks')
      .select('*, players!inner(full_name, position_abbreviation, mlb_team_abbreviation, headshot_url)')
      .eq('draft_id', draftId)
      .order('pick_number', { ascending: true });

    if (error) {
      console.error('Failed to fetch draft picks:', error);
      setLoading(false);
      return;
    }

    const mapped: DraftPick[] = (data || []).map((p: any) => {
      const team = leagueTeams.find(t => t.id === p.team_id);
      return {
        id: p.id,
        round: p.round,
        pick_number: p.pick_number,
        round_pick: p.round_pick,
        team_id: p.team_id,
        team_name: team?.name || 'Unknown',
        player_id: p.player_id,
        player_name: p.players.full_name,
        position: p.players.position_abbreviation,
        mlb_team: p.players.mlb_team_abbreviation,
        headshot_url: p.players.headshot_url,
        is_auto_pick: p.is_auto_pick,
      };
    });

    setPicks(mapped);
    setLoading(false);
  }, [draftId, leagueTeams]);

  useEffect(() => { fetchPicks(); }, [fetchPicks]);

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (picks.length === 0) {
    return (
      <Card className="bg-card border-border">
        <CardContent className="p-8 text-center">
          <Trophy className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <p className="font-medium text-foreground">No Draft Results</p>
          <p className="text-sm text-muted-foreground mt-1">No picks have been made yet.</p>
        </CardContent>
      </Card>
    );
  }

  // Group by round
  const rounds = new Map<number, DraftPick[]>();
  picks.forEach(p => {
    if (!rounds.has(p.round)) rounds.set(p.round, []);
    rounds.get(p.round)!.push(p);
  });

  // Group by team
  const teamGroups = new Map<string, DraftPick[]>();
  picks.forEach(p => {
    if (!teamGroups.has(p.team_id)) teamGroups.set(p.team_id, []);
    teamGroups.get(p.team_id)!.push(p);
  });

  // Unique teams for filter
  const uniqueTeams = Array.from(new Set(picks.map(p => p.team_id))).map(id => ({
    id,
    name: picks.find(p => p.team_id === id)?.team_name || 'Unknown',
  }));

  const totalRounds = Math.max(...picks.map(p => p.round));

  const PickRow = ({ pick, showTeam = true }: { pick: DraftPick; showTeam?: boolean }) => (
    <div className="flex items-center gap-3 py-2 px-3 rounded-lg hover:bg-muted/30 transition-colors">
      <span className="font-mono text-xs text-muted-foreground w-10 flex-shrink-0">
        {pick.round}.{pick.round_pick}
      </span>
      <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0 bg-muted/50">
        {pick.headshot_url ? (
          <img src={pick.headshot_url} alt={pick.player_name} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-xs text-muted-foreground">
            {pick.player_name.charAt(0)}
          </div>
        )}
      </div>
      <Badge
        variant="outline"
        className={cn('text-[10px] px-1.5 flex-shrink-0', positionColors[pick.position] || '')}
      >
        {pick.position}
      </Badge>
      <div className="min-w-0 flex-1" style={{ minWidth: '6rem' }}>
        <p className="text-sm font-medium text-foreground truncate">{pick.player_name}</p>
        {pick.mlb_team && (
          <p className="text-[10px] text-muted-foreground">{pick.mlb_team}</p>
        )}
      </div>
      {showTeam && (
        <span className="text-xs text-muted-foreground flex-shrink-0 max-w-[80px] truncate">
          {pick.team_name}
        </span>
      )}
      {pick.is_auto_pick && (
        <Badge variant="secondary" className="text-[9px] px-1 flex-shrink-0">Auto</Badge>
      )}
    </div>
  );

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold font-heading text-foreground flex items-center gap-2">
            <Trophy className="w-5 h-5 text-primary" />
            Draft Results
          </h2>
          <p className="text-sm text-muted-foreground">
            {picks.length} picks • {totalRounds} rounds • {uniqueTeams.length} teams
          </p>
        </div>
        <Tabs value={viewMode} onValueChange={(v) => setViewMode(v as 'round' | 'team')}>
          <TabsList className="h-8">
            <TabsTrigger value="round" className="px-3 h-7 gap-1.5 text-xs">
              <Layers className="w-3.5 h-3.5" />
              By Round
            </TabsTrigger>
            <TabsTrigger value="team" className="px-3 h-7 gap-1.5 text-xs">
              <Users className="w-3.5 h-3.5" />
              By Team
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* By Round View */}
      {viewMode === 'round' && (
        <div className="space-y-3">
          {Array.from(rounds.entries())
            .sort(([a], [b]) => a - b)
            .map(([round, roundPicks]) => (
              <Card key={round} className="bg-card border-border">
                <CardHeader className="pb-2 pt-3 px-4">
                  <CardTitle className="text-sm font-heading flex items-center gap-2">
                    Round {round}
                    <Badge variant="outline" className="text-[10px] ml-auto">
                      {roundPicks.length} picks
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="px-1 pb-2">
                  <div className="divide-y divide-border/30">
                    {roundPicks
                      .sort((a, b) => a.round_pick - b.round_pick)
                      .map(pick => (
                        <PickRow key={pick.id} pick={pick} showTeam />
                      ))}
                  </div>
                </CardContent>
              </Card>
            ))}
        </div>
      )}

      {/* By Team View */}
      {viewMode === 'team' && (
        <div className="space-y-3">
          {/* Team selector pills */}
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => setSelectedTeamId(null)}
              className={cn(
                'px-3 py-1.5 rounded-full text-xs font-medium border transition-colors',
                !selectedTeamId
                  ? 'bg-primary/15 border-primary/40 text-foreground'
                  : 'bg-muted/20 border-border/50 text-muted-foreground hover:bg-muted/40'
              )}
            >
              All Teams
            </button>
            {uniqueTeams.map(team => (
              <button
                key={team.id}
                onClick={() => setSelectedTeamId(team.id)}
                className={cn(
                  'px-3 py-1.5 rounded-full text-xs font-medium border transition-colors',
                  selectedTeamId === team.id
                    ? 'bg-primary/15 border-primary/40 text-foreground'
                    : 'bg-muted/20 border-border/50 text-muted-foreground hover:bg-muted/40'
                )}
              >
                {team.name}
              </button>
            ))}
          </div>

          {/* Team rosters */}
          {Array.from(teamGroups.entries())
            .filter(([teamId]) => !selectedTeamId || teamId === selectedTeamId)
            .sort(([, a], [, b]) => a[0].team_name.localeCompare(b[0].team_name))
            .map(([teamId, teamPicks]) => (
              <Card key={teamId} className="bg-card border-border">
                <CardHeader className="pb-2 pt-3 px-4">
                  <CardTitle className="text-sm font-heading flex items-center gap-2">
                    {teamPicks[0].team_name}
                    <Badge variant="outline" className="text-[10px] ml-auto">
                      {teamPicks.length} players
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="px-1 pb-2">
                  <div className="divide-y divide-border/30">
                    {teamPicks
                      .sort((a, b) => a.pick_number - b.pick_number)
                      .map(pick => (
                        <PickRow key={pick.id} pick={pick} showTeam={false} />
                      ))}
                  </div>
                </CardContent>
              </Card>
            ))}
        </div>
      )}
    </div>
  );
};

export default DraftResults;
